/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { useDataPersistenceService } from "../../services/backend/DataService";

export interface RedisTerminalProps {
  visible: boolean;
  onClose: () => void;
}

const terminalFontStyle = {
  fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
  fontSize: "13px",
  lineHeight: 1.6,
};

export const RedisTerminal = ({ visible, onClose }: RedisTerminalProps) => {
  const dataPersistenceService = useDataPersistenceService();
  const [output, setOutput] = useState("");
  const [input, setInput] = useState("");
  const [isRunning, setIsRunning] = useState(false);

  const outputRef = useRef<HTMLDivElement>(null);
  // Ref for the input element to maintain focus
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll output to bottom
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Reset state and focus when dialog opens
  useEffect(() => {
    if (visible) {
      setOutput("");
      setInput("");
      // Small timeout ensures the Dialog is rendered before focusing
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [visible]);

  const formatResult = (result: unknown): string => {
    if (result === null || result === undefined) {
      return "(nil)";
    }
    if (typeof result === "string") {
      return `"${result}"`;
    }
    if (Array.isArray(result)) {
      if (result.length === 0) {
        return "(empty array)";
      }
      return result.map((item, i) => `${i + 1}) ${formatResult(item)}`).join("\n");
    }
    if (typeof result === "object") {
      return JSON.stringify(result, null, 2);
    }
    return String(result);
  };

  const executeCommand = async () => {
    const trimmed = input.trim();
    if (!trimmed || isRunning) {
      return;
    }

    const parts = trimmed.split(/\s+/);
    const command = parts[0];
    const args = parts.slice(1);

    setInput("");
    setIsRunning(true);

    const currentOutput = output + `> ${trimmed}\n`;
    setOutput(currentOutput);

    try {
      const response = await dataPersistenceService.executeRedisCommand(command, args);
      const formatted = formatResult(response.result);
      setOutput(currentOutput + formatted + "\n\n");
    } catch (e: any) {
      const errorMessage = e.debugMessage || e.message || "An error occurred.";
      setOutput(currentOutput + `(error) ${errorMessage}\n\n`);
    } finally {
      setIsRunning(false);
      // Re-focus the input after the command is done and it's re-enabled
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeCommand();
    }
  };

  return (
    <Dialog open={visible} onClose={onClose} PaperProps={{ sx: { width: "100%", maxWidth: "80vw!important" } }}>
      <DialogTitle>Redis Terminal</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 0, pb: 0 }}>
        {/* Output area */}
        <Box
          ref={outputRef}
          sx={{
            ...terminalFontStyle,
            backgroundColor: "#000",
            color: "#0f0",
            padding: 2,
            height: "60vh",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            borderRadius: "4px 4px 0 0",
          }}
        >
          {output || "Connected. Type a Redis command and press Enter.\n\n"}
        </Box>
        {/* Input area */}
        <TextField
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. KEYS *; HGETALL Player;"
          disabled={isRunning}
          autoFocus
          variant="filled"
          inputRef={inputRef}
          autoComplete="off" // Fix 1: Disable Chrome suggestions
          slotProps={{
            htmlInput: {
              autoComplete: "off", // Extra insurance for some browsers
              spellCheck: false,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ color: "#0f0", ...terminalFontStyle }}>
                  {isRunning ? <CircularProgress size={14} sx={{ color: "#0f0" }} /> : ">"}
                </InputAdornment>
              ),
              sx: {
                ...terminalFontStyle,
                backgroundColor: "#000",
                color: "#0f0",
                borderRadius: "0 0 4px 4px",
                "&:hover": { backgroundColor: "#111" },
                "&.Mui-focused": { backgroundColor: "#111" },
                "& input": { color: "#0f0" },
                "& input::placeholder": { color: "#0a0", opacity: 0.6 },
              },
              disableUnderline: true,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOutput("")} disabled={!output}>
          Clear
        </Button>
        <Button onClick={onClose} color="error">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
